import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';

const BASE = import.meta.env.VITE_API_URL ?? '/api';

interface SriCert {
  id: string;
  issuer: string;
  subject_ruc: string;
  valid_from: string;
  valid_to: string;
  expires_in_days: number;
  is_active: boolean;
  uploaded_at: string;
}

export function SriCertificatePage() {
  const { t } = useTranslation('settings');
  const { activeOrgId } = useAuth();
  const qc = useQueryClient();

  const [password, setPassword] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sri-cert-active', activeOrgId],
    queryFn: () => api<SriCert | null>('/bookkeeping/sri/certificates/active'),
    enabled: !!activeOrgId,
  });

  const cert = data ?? null;

  const uploadMut = useMutation({
    mutationFn: async () => {
      const file = fileRef.current?.files?.[0];
      if (!file) throw new Error(t('sriCertificate.errors.select_file'));
      const form = new FormData();
      form.append('p12', file);
      form.append('password', password);
      const res = await fetch(`${BASE}/bookkeeping/sri/certificates`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = (body as { error?: string }).error ?? `HTTP ${res.status}`;
        throw new Error(code);
      }
      return res.json();
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sri-cert-active'] });
      setPassword('');
      setSubmitError(null);
      setSubmitSuccess(true);
      if (fileRef.current) fileRef.current.value = '';
      setTimeout(() => setSubmitSuccess(false), 2000);
    },
    onError: (e: unknown) => {
      const err = e as { message?: string };
      const code = err.message ?? 'unknown';
      setSubmitError(t(`sriCertificate.errors.${code}`, { defaultValue: code }));
    },
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) => api(`/bookkeeping/sri/certificates/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sri-cert-active'] });
    },
  });

  const expiresWarning = cert && cert.expires_in_days < 30;

  return (
    <div className="max-w-md space-y-6">
      <h1 className="text-xl font-semibold">{t('sriCertificate.title')}</h1>

      {/* Active cert card */}
      {!isLoading && cert && (
        <div
          className={`rounded border p-4 ${expiresWarning ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'}`}
        >
          <div className="mb-2 text-sm font-semibold text-slate-700">
            {t('sriCertificate.active')}
          </div>
          <dl className="space-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="text-slate-500">{t('sriCertificate.issuer')}:</dt>
              <dd>{cert.issuer}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-slate-500">{t('sriCertificate.ruc')}:</dt>
              <dd className="font-mono">{cert.subject_ruc}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-slate-500">{t('sriCertificate.validity')}:</dt>
              <dd>
                {cert.valid_from.slice(0, 10)} → {cert.valid_to.slice(0, 10)}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-slate-500">{t('sriCertificate.expires_in')}:</dt>
              <dd className={expiresWarning ? 'font-semibold text-amber-700' : ''}>
                {cert.expires_in_days} {t('sriCertificate.days')}
              </dd>
            </div>
          </dl>
          {expiresWarning && (
            <p className="mt-2 text-xs text-amber-700">{t('sriCertificate.expiry_warning')}</p>
          )}
          <button
            type="button"
            onClick={() => deactivateMut.mutate(cert.id)}
            disabled={deactivateMut.isPending}
            className="mt-3 rounded border border-red-300 px-3 py-1 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            {t('sriCertificate.deactivate')}
          </button>
        </div>
      )}

      {/* Upload form */}
      <div className="space-y-3 rounded border bg-white p-4">
        <h2 className="text-base font-semibold">
          {cert ? t('sriCertificate.replace') : t('sriCertificate.upload')}
        </h2>
        <label className="block">
          <span className="text-sm">{t('sriCertificate.file')}</span>
          <input
            ref={fileRef}
            type="file"
            accept=".p12,.pfx"
            className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:rounded file:border file:px-3 file:py-1 file:text-sm"
          />
        </label>
        <label className="block">
          <span className="text-sm">{t('sriCertificate.password')}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded border px-3 py-2 text-sm"
            autoComplete="current-password"
          />
        </label>
        <button
          type="button"
          disabled={uploadMut.isPending}
          onClick={() => {
            setSubmitError(null);
            uploadMut.mutate();
          }}
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          {uploadMut.isPending ? '…' : t('sriCertificate.submit')}
        </button>
        {submitSuccess && <span className="ml-2 text-sm text-green-600">✓</span>}
        {submitError && <p className="mt-1 text-sm text-red-600">{submitError}</p>}
      </div>
    </div>
  );
}
